import { GamePageSortDirection } from "../../../types";
import { useState, useEffect, useMemo, useCallback, startTransition } from "react";

interface UseTableSortProps {
	rawValues: Array<Array<any>>;
	renderedValues: Array<Array<any>>;
	sortableColumns?: boolean[];
}

/**
 * Compares two values for sorting
 */
function compareValues(valA: any, valB: any, direction: GamePageSortDirection): number {
	if (typeof valA === "number" && typeof valB === "number") {
		return direction === GamePageSortDirection.Asc ? valA - valB : valB - valA;
	}
	return direction === GamePageSortDirection.Asc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
}

/**
 * Hook for managing table sorting state and logic
 */
export function useTableSort({ rawValues, renderedValues, sortableColumns }: UseTableSortProps) {
	const [sortColumn, setSortColumn] = useState<number | null>(null);
	const [sortDirection, setSortDirection] = useState<GamePageSortDirection | null>(null);

	// Memoize sorted indexes to avoid recalculation
	const sortedIndexes = useMemo(() => {
		if (sortColumn == null || sortDirection == null || rawValues.length === 0) {
			return Array.from({ length: rawValues.length }, (_, i) => i);
		}

		return [...Array(rawValues.length).keys()].sort((a, b) => {
			const valA = rawValues[a][sortColumn];
			const valB = rawValues[b][sortColumn];
			return compareValues(valA, valB, sortDirection);
		});
	}, [rawValues, sortColumn, sortDirection]);

	// Memoize displayed values based on sorted indexes
	const displayedValues = useMemo(() => {
		return sortedIndexes.map((i) => renderedValues[i]);
	}, [sortedIndexes, renderedValues]);

	// Reset to original order when renderedValues change
	useEffect(() => {
		startTransition(() => {
			setSortColumn(null);
			setSortDirection(null);
		});
	}, [renderedValues, rawValues.length]);

	const handleSort = useCallback(
		(colIndex: number) => {
			if (sortableColumns && !sortableColumns[colIndex]) return; // skip if not sortable

			let direction: GamePageSortDirection | null = GamePageSortDirection.Asc;
			if (sortColumn === colIndex && sortDirection === GamePageSortDirection.Asc) {
				direction = GamePageSortDirection.Desc;
			} else if (sortColumn === colIndex && sortDirection === GamePageSortDirection.Desc) {
				direction = null;
			}

			setSortColumn(direction ? colIndex : null);
			setSortDirection(direction);
		},
		[sortColumn, sortDirection, sortableColumns]
	);

	/**
	 * Maps an original index to its position in the sorted array
	 */
	const getTargetIndex = useCallback(
		(originalIndex: number | null): number | null => {
			if (originalIndex == null) return null;

			if (sortColumn == null || sortDirection == null) {
				return originalIndex;
			}

			const mappedIndex = sortedIndexes.indexOf(originalIndex);
			return mappedIndex >= 0 ? mappedIndex : null;
		},
		[sortedIndexes, sortColumn, sortDirection]
	);

	return {
		sortColumn,
		sortDirection,
		displayedValues,
		indexMapping: sortedIndexes,
		handleSort,
		getTargetIndex,
	};
}
